import React, { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import {
  NotificationContext,
  TestDetailsContext,
  TestResultContext,
  ServiceContext,
} from "@app/provider";
import { TestStatus } from "@app/provider/testData/types";
import { routes } from "@app/routing";
import PdfView from "@app/components/pdf/pdfView";
import { BlueHeaderContent } from "@app/components/ui/layout";

import { NevaWrapper } from "@app/neva/nevaWrapper";
import "./testResult.scss";
import { usePdfFile } from "@app/hooks";
import { getPdfViewHeapHandlers, isFirstResultView } from "@app/utils";
import { usePippaAnalytics } from "@app/pippa/hooks/usePippaAnalytics";
import { PippaIngressSource } from "@app/pippa/models/analytics";

const messages = defineMessages({
  testResultResultsDocument: {
    id: "testResultResultsDocument",
    defaultMessage: "Test Results",
  },
  testResultCloseResults: {
    id: "testResultCloseResults",
    defaultMessage: "Close",
  },
  testResultPdfAriaLabel: {
    id: "testResultPdfAriaLabel",
    defaultMessage: "PDF document with test results",
  },
});

export type TestResultParams = {
  orderUidPublic: string;
  testUid: string;
  testDocumentUid: string;
};

// UPP-2494: session-scoped dedup for the VIEWED mutation.
// Keys survive until a full page reload
const viewedDocuments = new Set<string>();

const TestResultPage: React.FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { orderUidPublic, testUid, testDocumentUid } = useParams<
    keyof TestResultParams
  >() as TestResultParams;

  const { accessTestResults } = useContext(TestResultContext);
  const { addNotification } = useContext(NotificationContext);
  const { fileService } = React.useContext(ServiceContext);
  const { getTestDetails } = useContext(TestDetailsContext);
  const { trackIngress } = usePippaAnalytics();
  const testDetails = getTestDetails();

  const resultUrl = `/result/${orderUidPublic}/${testUid}/${testDocumentUid}`;
  const { file, pdfTokenError } = usePdfFile({ url: `/api/1.0${resultUrl}` });
  const { onPdfViewErrorHeap, onPdfViewSuccessHeap } = getPdfViewHeapHandlers(
    testDetails,
    orderUidPublic,
    testDocumentUid
  );

  // Latch the first-view verdict once testDetails resolves. This survives a
  // subsequent viewStatus refresh so the Pippa auto-open (in closePdf) stays
  // armed after accessTestResults marks the doc VIEWED.
  const wasFirstView = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (wasFirstView.current === undefined && testDetails) {
      wasFirstView.current = isFirstResultView(testDetails);
    }
  }, [testDetails]);

  // UPP-2494: only mark the test VIEWED once react-pdf reports the document
  // actually rendered. No mount-time write — opening the page is not proof of
  // viewing the result. Errors surface as a toast but must never block the
  // user's PDF flow.
  const onPdfLoadSuccess = () => {
    if (!testDetails?.testType) return;
    const key = `${orderUidPublic}:${testUid}:${testDocumentUid}`;
    if (viewedDocuments.has(key)) return;
    viewedDocuments.add(key);
    onPdfViewSuccessHeap();
    accessTestResults(
      orderUidPublic,
      testUid,
      testDetails.testType,
      testDocumentUid,
      TestStatus.VIEWED
    ).catch(() => {
      // Non-fatal: the PDF has already rendered. We do NOT unmark the key
      // here — one failed attempt per session per doc is enough; a persistent
      // loop of remounts would otherwise keep retrying and drown the user in
      // toasts. A page reload gets a fresh chance.
      addNotification({ type: "error" });
    });
  };

  const onDownloadPdf = async () => {
    const fileName = "Result.pdf";
    const result = await fileService.getFile(resultUrl);
    fileService.download(result, fileName);
  };

  const closePdf = () => {
    const openChatbot =
      wasFirstView.current === true && testDetails?.isPippaEnabled === true;

    if (openChatbot) {
      trackIngress(PippaIngressSource.AUTO_OPEN_POST_PDF);
    }

    navigate(routes.testResultDetailsPage(orderUidPublic, testUid), {
      replace: true,
      state: { ...location.state, autoOpenPippa: openChatbot },
    });
  };

  return (
    <>
      <div className="test-result" data-testid="test-result-pdf-page">
        <BlueHeaderContent
          blueHeaderTitle={intl.formatMessage(
            messages.testResultResultsDocument
          )}
          crossHeaderButton={closePdf}
          fullScreen
        >
          <PdfView
            file={file}
            onDownload={onDownloadPdf}
            onPdfViewError={onPdfViewErrorHeap}
            onPdfViewSuccess={onPdfLoadSuccess}
            pageAriaLabel={intl.formatMessage(messages.testResultPdfAriaLabel)}
            hasError={pdfTokenError}
          />
        </BlueHeaderContent>
      </div>
      <NevaWrapper hideIfResultViewed={true} />
    </>
  );
};

export default TestResultPage;
